import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export default function ComplaintsPage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="complaints-page">
      <Header />

      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-8 font-['Outfit']">Complaints Procedure</h1>
          <p className="text-slate-600 mb-8">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">Our Commitment</h2>
              <p className="text-slate-600 leading-relaxed">
                At QuickFinance.io, we are committed to providing excellent service. However, we understand that sometimes things may go wrong. If you are unhappy with any aspect of our service, we want to hear from you and will do our best to put things right.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">What is a Complaint?</h2>
              <p className="text-slate-600 leading-relaxed">
                A complaint is any expression of dissatisfaction about our service, whether oral or written, and whether justified or not, from or on behalf of a person about the provision of, or failure to provide, a financial service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">How to Make a Complaint</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                You can make a complaint in any of the following ways:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="font-semibold text-slate-900 mb-2">By Email</h3>
                  <p className="text-slate-600">complaints@quickfinance.io</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-6 md:col-span-2">
                  <h3 className="font-semibold text-slate-900 mb-2">By Post</h3>
                  <p className="text-slate-600">
                    Complaints Department<br />
                    QuickFinance.io<br />
                    London, United Kingdom
                  </p>
                </div>
              </div>

              <p className="text-slate-600 leading-relaxed">
                When making a complaint, please include:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li>Your full name and contact details</li>
                <li>Your application reference number (if applicable)</li>
                <li>A clear description of your complaint</li>
                <li>What you would like us to do to resolve the issue</li>
                <li>Any relevant supporting documents</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">Our Complaints Process</h2>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-700 font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Acknowledgement</h3>
                    <p className="text-slate-600">We will acknowledge your complaint within 3 business days of receiving it.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-700 font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Investigation</h3>
                    <p className="text-slate-600">A senior member of our team will investigate your complaint thoroughly and impartially.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-700 font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Resolution</h3>
                    <p className="text-slate-600">We aim to resolve complaints within 8 weeks. For simpler issues, we will try to resolve within 3 business days.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-700 font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Final Response</h3>
                    <p className="text-slate-600">We will provide you with a written final response explaining our findings and any action we will take.</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">If You're Not Satisfied</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                If you are not satisfied with our final response, or if 8 weeks have passed since you made your complaint, you may be able to refer your complaint to the Financial Ombudsman Service (FOS).
              </p>
              
              <div className="bg-slate-50 rounded-xl p-6 mb-4">
                <h3 className="font-semibold text-slate-900 mb-3">Financial Ombudsman Service</h3>
                <p className="text-slate-600 mb-2">Exchange Tower</p>
                <p className="text-slate-600 mb-2">London, E14 9SR</p>
                <p className="text-slate-600 mb-2">Phone: 0800 023 4567 (free from UK landlines and mobiles)</p>
                <p className="text-slate-600 mb-2">Email: complaint.info@financial-ombudsman.org.uk</p>
                <p className="text-slate-600">Website: www.financial-ombudsman.org.uk</p>
              </div>
              
              <p className="text-slate-600 leading-relaxed">
                The Financial Ombudsman Service is a free, independent service for resolving disputes between consumers and financial businesses. You must refer your complaint to the FOS within 6 months of our final response.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">Complaints About Our Funding Partners</h2>
              <p className="text-slate-600 leading-relaxed">
                If your complaint relates to a funding partner rather than QuickFinance.io, we will help direct your complaint to the appropriate party. Funding partners have their own complaints procedures, which they will explain to you.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">Our Promise</h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                We take all complaints seriously and use them as an opportunity to improve our service. Every complaint is reviewed by senior management to identify any systemic issues and implement improvements.
              </p>
              
              <Link to="/contact">
                <Button className="bg-blue-700 hover:bg-blue-800" data-testid="contact-btn">
                  Contact Us <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
